'use client'

import { useEffect, useRef } from 'react'
import { cn } from '@/utils/styles/cn'

export interface IMenuCategory {
    uuid: string
    name: string
}

interface IRestaurantDetailMenuCategoryBarProps {
    categories: IMenuCategory[]
    activeUuid: string | null
    onSelect: (uuid: string) => void
}

/**
 * Sticky category chip bar inside the menu section. Sticks right below the
 * page's section tab bar (plus the mobile site navigation above it).
 */
export const RestaurantDetailMenuCategoryBar = ({
    categories,
    activeUuid,
    onSelect,
}: IRestaurantDetailMenuCategoryBarProps) => {
    const rowRef = useRef<HTMLDivElement>(null)
    const chipRefs = useRef<Record<string, HTMLButtonElement | null>>({})

    // Keep the active chip visible while the scroll spy walks through the
    // categories. Horizontal scroll on the row only, scrollIntoView could
    // also move the page vertically under the sticky bars.
    useEffect(() => {
        const row = rowRef.current
        const chip = activeUuid ? chipRefs.current[activeUuid] : null

        if (!row || !chip) return

        row.scrollTo({
            behavior: 'smooth',
            left: chip.offsetLeft - (row.clientWidth - chip.clientWidth) / 2,
        })
    }, [activeUuid])

    return (
        <div className="sticky top-[114px] md:top-[50px] z-[2] bg-bg-neutral-base py-2 mb-4 -mx-4 w-[calc(100%+32px)] md:mx-0 md:w-full">
            <div ref={rowRef} className="flex gap-2 overflow-x-auto scrollbar-hide px-4 md:px-0">
                {categories.map((category) => {
                    const isActive = category.uuid === activeUuid

                    return (
                        <button
                            type="button"
                            ref={(element) => {
                                chipRefs.current[category.uuid] = element
                            }}
                            key={'menu-category' + category.uuid}
                            onClick={() => onSelect(category.uuid)}
                            className={cn(
                                `flex h-10 shrink-0 items-center rounded-full px-4 text-label-md
                                whitespace-nowrap cursor-pointer select-none webkit-tap-transparent
                                transition-colors duration-150`,
                                isActive
                                    ? 'bg-bg-inverse-neutral text-fg-inverse'
                                    : 'bg-bg-neutral-surface text-fg-primary hover:bg-bg-neutral-surface-medium'
                            )}
                        >
                            {category.name}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}
